import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_dmtai-ecosystem/artifacts/a7g76rw9_DMTAI%20Leo.png';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/services', label: t('nav.services') },
  ];

  const isActive = (path) => location.pathname === path;

  const handleHomeClick = (e, path, closeMenu = false) => {
    // If clicking Home while already on Home, smooth-scroll to top
    if (path === '/' && location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (closeMenu) setIsOpen(false);
    } else if (path === '/' && location.pathname !== '/') {
      // Navigate to home; ScrollToTop component will handle scroll on route change
      e.preventDefault();
      navigate('/');
      if (closeMenu) setIsOpen(false);
    } else if (closeMenu) {
      setIsOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-4 py-3 sm:py-4">
      <div className="container-custom">
        <div className="glass-strong rounded-2xl px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          {/* Logo — always visible on all screen sizes */}
          <Link to="/" onClick={(e) => handleHomeClick(e, '/')}
                className="flex items-center gap-2 sm:gap-3 group flex-shrink-0" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="DMTAI Strategies Logo"
              className="h-9 w-9 sm:h-10 sm:w-10 rounded-full object-cover transition-transform group-hover:scale-110 flex-shrink-0"
            />
            <span className="font-bold text-sm sm:text-lg tracking-tight whitespace-nowrap">
              <span className="gradient-text-gold">DMTAI</span>
              <span className="text-text-secondary ml-1 hidden xs:inline sm:inline">Strategies</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={(e) => handleHomeClick(e, item.path)}
                data-testid={`nav-link-${item.path.replace('/', '') || 'home'}`}
                className={`text-sm font-medium transition-all duration-300 hover:text-[var(--accent-gold)] ${
                  isActive(item.path)
                    ? 'text-[var(--accent-gold)]'
                    : 'text-[var(--text-secondary)]'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full border border-[var(--border-subtle)] hover:border-[var(--primary-main)] transition-all duration-300 text-xs sm:text-sm"
            >
              <Globe className="w-4 h-4 text-[var(--primary-light)]" />
              <span className="text-[var(--text-secondary)] uppercase font-medium">{language}</span>
            </button>

            {/* CTA Button */}
            <a
              href="https://calendly.com/dmt-a-master"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-book-call-btn"
              className="hidden sm:block btn-primary text-sm"
            >
              {t('nav.bookCall')}
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
              className="md:hidden p-2 rounded-lg hover:bg-[var(--bg-subtle)] transition-colors"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="md:hidden mt-2 glass-strong rounded-2xl p-4"
            >
              <div className="flex flex-col gap-3">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={(e) => handleHomeClick(e, item.path, true)}
                    data-testid={`mobile-nav-link-${item.path.replace('/', '') || 'home'}`}
                    className={`text-base font-medium py-2 px-4 rounded-lg transition-all duration-300 ${
                      isActive(item.path)
                        ? 'bg-[var(--primary-main)] bg-opacity-20 text-[var(--accent-gold)]'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <a
                  href="https://calendly.com/dmt-a-master"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-book-call-btn"
                  className="btn-primary text-center"
                >
                  {t('nav.bookCall')}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}
